/**
 * Utility functions for decoding Microsoft 365 Safe Links URLs
 */

export interface SafeLinksInfo {
  isValid: boolean;
  originalUrl: string | null;
  region: string | null;
  domain: string | null;
  data: string | null;
  sdata: string | null;
  reserved: string | null;
  error?: string;
}

/**
 * Check if a URL is a Microsoft 365 Safe Links URL
 */
export const isSafeLinksUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();
    
    // Check if it's a safelinks.protection.outlook.com domain
    return hostname.includes('safelinks.protection.outlook.com');
  } catch {
    return false;
  }
};

/**
 * Decode a Safe Links URL and extract the original URL
 */
export const decodeSafeLinksUrl = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const urlObj = new URL(url.trim());
    
    // Extract the 'url' parameter which contains the encoded original URL
    const encodedUrl = urlObj.searchParams.get('url');
    
    if (!encodedUrl) {
      return null;
    }

    // Decode the URL-encoded string
    const decodedUrl = decodeURIComponent(encodedUrl);
    
    // Validate that the decoded string is a valid URL
    try {
      new URL(decodedUrl);
      return decodedUrl;
    } catch {
      return null;
    }
  } catch {
    return null;
  }
};

/**
 * Extract detailed information from a Safe Links URL
 */
export const extractSafeLinksInfo = (url: string): SafeLinksInfo => {
  const info: SafeLinksInfo = {
    isValid: false,
    originalUrl: null,
    region: null,
    domain: null,
    data: null,
    sdata: null,
    reserved: null,
  };

  if (!url || typeof url !== 'string') {
    info.error = 'Invalid URL input';
    return info;
  }

  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Safe Links URL
    if (!hostname.includes('safelinks.protection.outlook.com')) {
      info.error = 'Not a Microsoft 365 Safe Links URL';
      return info;
    }

    info.isValid = true;
    info.domain = hostname;

    // Extract region (e.g., emea01, nam01, etc.)
    const regionMatch = hostname.match(/^([a-z0-9]+)\./i);
    if (regionMatch) {
      info.region = regionMatch[1];
    }

    // Extract the original URL
    const encodedUrl = urlObj.searchParams.get('url');
    if (encodedUrl) {
      try {
        const decodedUrl = decodeURIComponent(encodedUrl);
        // Validate the decoded URL
        new URL(decodedUrl);
        info.originalUrl = decodedUrl;
      } catch {
        info.error = 'Failed to decode original URL';
      }
    } else {
      info.error = 'No URL parameter found';
      info.isValid = false;
    }

    // Extract metadata parameters
    info.data = urlObj.searchParams.get('data');
    info.sdata = urlObj.searchParams.get('sdata');
    info.reserved = urlObj.searchParams.get('reserved');

    return info;
  } catch {
    info.error = 'Failed to parse URL';
    return info;
  }
};

/**
 * Validate if a string is a valid URL
 */
export const validateUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    new URL(url.trim());
    return true;
  } catch {
    return false;
  }
};

/**
 * Process multiple Safe Links URLs (batch processing)
 */
export const decodeBatchSafeLinks = (
  urls: string[]
): Array<{ input: string; output: string | null; error?: string }> => {
  return urls.map((url) => {
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
      return { input: url, output: null, error: 'Empty URL' };
    }

    if (!isSafeLinksUrl(trimmedUrl)) {
      return { input: url, output: null, error: 'Not a Safe Links URL' };
    }

    const decoded = decodeSafeLinksUrl(trimmedUrl);
    
    if (!decoded) {
      return { input: url, output: null, error: 'Failed to decode' };
    }

    return { input: url, output: decoded };
  });
};

/**
 * Get an example Safe Links URL for testing
 */
export const getExampleSafeLinksUrl = (): string => {
  return 'https://emea01.safelinks.protection.outlook.com/?url=https%3A%2F%2Fwww.strato.de%2Ffaq%2F&data=05%7C02%7C%7C19e2fb6f67744d7dfef808de0ca6fc4a%7C84df9e7fe9f640afb435aaaaaaaaaaaa%7C1%7C0%7C638962108486769122%7CUnknown%7CTWFpbGZsb3d8eyJFbXB0eU1hcGkiOnRydWUsIlYiOiIwLjAuMDAwMCIsIlAiOiJXaW4zMiIsIkFOIjoiTWFpbCIsIldUIjoyfQ%3D%3D%7C0%7C%7C%7C&sdata=pdEfeE5INb6Zu%2FvshjcfbKgNGwZrd2Qt3z6uKbQPWHM%3D&reserved=0';
};

